import React, { useState, useEffect } from 'react';
import './ImageGallery.css';
import { ArtworkImageStub } from "../../models/Artwork";
import {
    Typography,
    Box,
    IconButton,
    Tooltip,
    Button,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';

// Image Gallery
import ImageUploadWithDialog from '../../components/ImageUpload/ImageUploadDialogue';
import { ValidationResult } from "../../components/ImageUpload/validateImageFiles";
import { ArtworkImages } from "../../models/Artwork";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

// API
import { useUser } from '../../components/UserContext';
import { useApiClient } from "../../components/useApiClient";

interface ImageGalleryProps {
    artworkId: number;
    images: ArtworkImageStub[] | undefined;
    artworkTitle: string | undefined;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ artworkId, images, artworkTitle }) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
    const [hoveredImage, setHoveredImage] = useState<string | null>(null);
    const [showManagementDialog, setShowManagementDialog] = useState<boolean>(false);
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [imageVersion, setImageVersion] = useState(0);
    const [showAllImages, setShowAllImages] = useState(false);

    const [uploadedImages, setUploadedImages] = useState<FileList | null>(null);
    const [existingImages, setExistingImages] = useState<ArtworkImageStub[]>([]);
    const [artworkImages, setArtworkImages] = useState<{
        existing: ArtworkImageStub[];
        added: File[];
        removed: number[];
    }>({ existing: [], added: [], removed: [] });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;

    React.useEffect(() => {
        clientIdRef.current = activeClientId;
    }, [activeClientId]);

    const clientIdRef = React.useRef<number | null>(null);
    const { get, put } = useApiClient();

    useEffect(() => {
        const loadImages = async () => {
            try {
                setLoading(true);

                const accessToken = await getAccessToken();
                if (!accessToken) return;

                const result = await get<ArtworkImages>(`image/artwork/${artworkId}`, accessToken);
                setExistingImages(result.imageUrls);
            } catch (e: any) {
                console.error("Load images error", e?.message);
            } finally {
                setLoading(false);
            }
        };

        void loadImages();
    }, [artworkId, imageVersion]);

    useEffect(() => {
        if (selectedImageIndex === null) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                handleCloseModal();
            } else if (e.key === "ArrowLeft") {
                handlePreviousImage();
            } else if (e.key === "ArrowRight") {
                handleNextImage();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedImageIndex, existingImages.length]);

    const handleImageClick = (index: number) => {
        setSelectedImageIndex(index);
    };

    const handleCloseModal = () => {
        setSelectedImageIndex(null);
    };

    const handlePreviousImage = () => {
        if (selectedImageIndex === null || existingImages.length === 0) return;

        setSelectedImageIndex((prev) => {
            if (prev === null) return null;
            return prev === 0 ? existingImages.length - 1 : prev - 1;
        });
    };

    const handleNextImage = () => {
        if (selectedImageIndex === null || existingImages.length === 0) return;

        setSelectedImageIndex((prev) => {
            if (prev === null) return null;
            return prev === existingImages.length - 1 ? 0 : prev + 1;
        });
    };

    const handleMouseEnter = (imageUrl: string) => {
        if (!isMobile) {
            setHoveredImage(imageUrl);
        }
    };

    const handleMouseLeave = () => {
        setHoveredImage(null);
    };

    const selectedImage =
        selectedImageIndex !== null && existingImages[selectedImageIndex]
            ? existingImages[selectedImageIndex]
            : null;

    const getHighResUrl = (thumbUrl: string): string => {
        return thumbUrl.replace('thb_', '')
            .replace('w=300', 'w=1200')
            .replace('q=80', 'q=95');
    };

    const handleShowDialog = () => {
        setShowManagementDialog(true);
    };

    const handleDialogClose = () => {
        setShowManagementDialog(false);
    };

    const handleGalleryUpdate = (files: File[] | null, validationResult: ValidationResult | null) => {
        if (validationResult && !validationResult.isValid) {

            setUploadErrors(validationResult.errors);
            return;
        }

        if (files && files.length !== 0) {
            const dataTransfer = new DataTransfer();

            if (!uploadedImages) {
                files.forEach(file => dataTransfer.items.add(file));
            } else {
                Array.from(uploadedImages).forEach(file => dataTransfer.items.add(file));
                files.forEach(file => dataTransfer.items.add(file));
            }

            setUploadedImages(dataTransfer.files);
        }
    };

    const handleRemoveExistingGalleryImage = (_images: ArtworkImageStub[]) => {
        // optional parent sync
    };

    const handleRemovedGalleryImage = (_images: number[]) => {
        // optional parent sync
    };

    const handleImagesUpdate = async (updates: {
        existing?: ArtworkImageStub[];
        added?: File[];
        removed?: number[];
    }) => {
        setArtworkImages(prev => ({
            existing: [...prev.existing, ...(updates.existing || [])],
            added: [...prev.added, ...(updates.added || [])],
            removed: [...prev.removed, ...(updates.removed || [])]
        }));

        const accessToken = await getAccessToken();
        if (!accessToken) return;

        if ((updates.added?.length ?? 0) !== 0 || (updates.removed?.length ?? 0) !== 0) {
            try {
                const formData = new FormData();
                formData.append("ArtworkId", artworkId.toString());

                if (updates.removed) {
                    updates.removed.forEach(id => {
                        formData.append('RemovedImageIds', id.toString());
                    });
                }

                if (updates.added) {
                    Array.from(updates.added).forEach(img => formData.append('Images', img));
                }

                await put(`image/${artworkId}`, accessToken, formData);
                setImageVersion(prev => prev + 1);
            } catch {
                alert('Error updating images');
            }
        }
    };

    const [settingPrimary, setSettingPrimary] = useState<number | null>(null);

    const handleSetPrimary = async (imageId: number) => {
        if (settingPrimary) return;

        setSettingPrimary(imageId);

        try {
            const formData = new FormData();
            formData.append("ArtworkId", artworkId.toString());

            const accessToken = await getAccessToken();
            if (!accessToken) return;

            await put(`image/set-primary/${imageId}`, accessToken, formData);
            setImageVersion(prev => prev + 1);
        } catch (error) {
            console.error('Failed to set primary image:', error);
        } finally {
            setSettingPrimary(null);
        }
    };

    const primaryImage =
        existingImages.find(img => img.isPrimary) ??
        existingImages[0] ??
        null;

    const renderImageCard = (image: ArtworkImageStub, index: number, large = false) => (
        <Box
            key={image.imageId}
            sx={{
                position: "relative",
                border: image.isPrimary ? "2px solid" : "1px solid",
                borderColor: image.isPrimary ? "primary.main" : "grey.300",
                borderRadius: 2,
                p: 1,
                transition: "all 0.2s",
            }}
        >
            <div
                className="image-item"
                onMouseEnter={() => handleMouseEnter(image.imageUrl)}
                onMouseLeave={handleMouseLeave}
                onClick={() => handleImageClick(index)}
            >
                <img
                    src={image.imageUrl}
                    alt={`${artworkTitle} - View ${index + 1}`}
                    className={large ? "thumbnail mobile-primary-image" : "thumbnail"}
                    loading="lazy"
                />

                {!isMobile && (
                    <div className="image-overlay">
                        <span className="zoom-icon">🔍</span>
                    </div>
                )}

                {canEditArtwork && (
                    <Tooltip title="Set as primary image">
                        <IconButton
                            size="small"
                            color={image.isPrimary ? "warning" : "default"}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSetPrimary(image.imageId);
                            }}
                            sx={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                zIndex: 2,
                                backgroundColor: "background.paper",
                                "&:hover": {
                                    backgroundColor: image.isPrimary
                                        ? "warning.light"
                                        : "action.hover",
                                },
                            }}
                        >
                            {image.isPrimary ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                )}

                {image.isPrimary && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: 8,
                            left: 8,
                            padding: "2px 6px",
                            fontSize: "0.7rem",
                            borderRadius: 4,
                            backgroundColor: "rgba(255, 193, 7, 0.9)",
                            color: "#000",
                        }}
                    >
                        Primary
                    </div>
                )}
            </div>
        </Box>
    );

    if (loading) return <p>loading...</p>;

    return (
        <>
            <section className="artwork-section images-section">
                {/*<div className="section-header">*/}
                {/*    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>*/}
                {/*        <Typography variant="h3">Images</Typography>*/}
                {/*        <Button*/}
                {/*            variant="outlined"*/}
                {/*            size="small"*/}
                {/*            startIcon={<AddPhotoAlternateIcon />}*/}
                {/*            onClick={handleShowDialog}*/}
                {/*            hidden={!canEditArtwork}*/}
                {/*        >*/}
                {/*            Manage Images*/}
                {/*        </Button>*/}
                {/*    </Box>                    */}
                {/*</div>*/}

                <div className="section-content">
                    {existingImages.length > 0 ? (
                        isMobile ? (
                            <Box>
                                {primaryImage && renderImageCard(primaryImage, 0, true)}

                                {existingImages.length > 1 && (
                                    <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            onClick={() => setShowAllImages(prev => !prev)}
                                        >
                                            {showAllImages
                                                ? "Hide gallery"
                                                : `View all images (${existingImages.length})`}
                                        </Button>
                                    </Box>
                                )}

                                {showAllImages && (
                                    <div className="images-grid" style={{ marginTop: "12px" }}>
                                        {existingImages.map((image, index) => (
                                            <React.Fragment key={image.imageId}>
                                                {renderImageCard(image, index)}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </Box>
                        ) : (
                            <div className="images-grid">
                                {existingImages.map((image, index) => (
                                    <React.Fragment key={image.imageId}>
                                        {renderImageCard(image, index)}
                                    </React.Fragment>
                                ))}
                            </div>
                        )
                    ) : (
                        <div className="no-images">
                            <div className="no-images-icon">🖼️</div>
                            <p>No artwork images added</p>
                        </div>
                    )}
                    <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            startIcon={<AddPhotoAlternateIcon />}
                            onClick={handleShowDialog}
                            hidden={!canEditArtwork}
                        >
                            Manage Images
                        </Button>
                    </Box>
                </div>
            </section>

            {!isMobile && hoveredImage && (
                <div className="image-preview">
                    <div className="preview-content">
                        <img
                            src={getHighResUrl(hoveredImage)}
                            alt="Preview"
                            className="preview-image"
                        />
                    </div>
                </div>
            )}

            {selectedImage && (
                <div className="image-modal" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="close-button" onClick={handleCloseModal}>
                            ×
                        </button>

                        {existingImages.length > 1 && (
                            <>
                                <button
                                    className="image-nav-button image-nav-button-left"
                                    onClick={handlePreviousImage}
                                    aria-label="Previous image"
                                >
                                    ‹
                                </button>

                                <button
                                    className="image-nav-button image-nav-button-right"
                                    onClick={handleNextImage}
                                    aria-label="Next image"
                                >
                                    ›
                                </button>
                            </>
                        )}

                        <img
                            src={getHighResUrl(selectedImage.imageUrl)}
                            alt={`${artworkTitle} - Full size`}
                            className="modal-image"
                        />

                        <div className="image-info">
                            <span>{artworkTitle}</span>
                            {selectedImageIndex !== null && existingImages.length > 1 && (
                                <span style={{ marginLeft: "12px", opacity: 0.8 }}>
                                    {selectedImageIndex + 1} / {existingImages.length}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ImageUploadWithDialog
                triggerButtonPosition="absolute"
                existingImages={existingImages}
                onImagesChange={(files, val) => handleGalleryUpdate(files, val)}
                onExistingImagesChange={(revisedImageList) => handleRemoveExistingGalleryImage(revisedImageList)}
                removedImages={(removedImageList) => handleRemovedGalleryImage(removedImageList)}
                maxFiles={10}
                dialogTitle="Manage Images"
                dialogMaxWidth="sm"
                showDialog={showManagementDialog}
                onDialogClose={handleDialogClose}
                onImagesUpdate={handleImagesUpdate}
            />
        </>
    );
};

export default ImageGallery;